import { HeartCrack } from "lucide-react";
import { type QuizConfig } from "~/types/quiz";

export const procenaPartnerskeNarcisoidnosti: QuizConfig = {
  id: "procena-partnerske-narcisoidnosti",
  illustration: {
    src: "/images/quizzes/procena-partnerske-narcisoidnosti.webp",
    alt: "Žena pokušava da razgovara dok partner gleda u telefon.",
  },
  lastModified: "2026-10-05",
  title: "Da li je tvoj partner narcis?",
  shortTitle: "Procena narcisoidnosti partnera",
  description:
    "Otkrij da li tvoj partner ispoljava narcisoidne osobine i kako to može uticati na vašu vezu.",
  longDescription:
    "Proceni da li u vezi postoje obrasci kontrole, omalovažavanja ili manipulacije. Dobijaš savete kako da postaviš granice i zaštitiš sebe.",
  icon: HeartCrack,
  duration: "6-8 min",
  category: "Odnosi i komunikacija",
  relatedDirectorySlugs: ["odnosi", "emocionalno-zlostavljanje"],
  focusAreasWanted: [
    "narcissistic_abuse",
    "couples_issues",
    "boundaries",
    "relationships",
  ],
  safety: {
    urgentOptionTexts: [
      "Koristi pretnje ili manipulaciju da bi pobedio/la",
      "Napada, manipuliše ili preti",
    ],
  },
  questions: [
    {
      type: "radio",
      question: "Pol tvog partnera:",
      options: ["Muški", "Ženski", "Ne želim da se izjasnim"],
      scores: [0, 0, 0],
    },
    {
      type: "radio",
      question: "Kako tvoj partner reaguje na kritiku?",
      options: [
        "Prihvata je i pokušava da se poboljša",
        "Ponekad se uzruja, ali kasnije prihvati",
        "Odmah postaje defanzivan/na",
        "Napada i okrivljuje mene umesto da prihvati kritiku",
        "Potpuno odbacuje kritiku i postaje agresivan/na",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Kako tvoj partner reaguje kada ne dobije pažnju koju želi?",
      options: [
        "Razume i ne pravi problem",
        "Ponekad bude povređen/a, ali ne preteruje",
        "Zahteva pažnju i nervira se ako je ne dobije",
        "Ljutito se povlači ili kažnjava ćutanjem",
        "Pravi scene ili koristi emocionalne ucene",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question: "Kako tvoj partner doživljava sebe? (označi sve što važi)",
      options: [
        "Misli da je posebna osoba i bolji/a od drugih",
        "Ima potrebu da bude u centru pažnje",
        "Veruje da zaslužuje posebne privilegije",
        "Ponaša se kao da su drugi ispod njega/nje",
        "Ne pokazuje takve osobine",
      ],
      scores: [2, 2, 2, 2, 0],
    },
    {
      type: "radio",
      question: "Kako tvoj partner reaguje kada neko drugi postigne uspeh?",
      options: [
        "Raduje se iskreno",
        "Ponekad bude ljubomoran/na, ali to ne pokazuje",
        "Minimalizuje njihov uspeh ili nalazi mane",
        "Naljuti se i oseća ugroženim/om",
        "Mora da preusmeri pažnju na sebe i svoj uspeh",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Da li tvoj partner menja raspoloženja bez očiglednog razloga?",
      options: [
        "Retko, ima stabilno raspoloženje",
        "Ponekad, ali mogu da razumem zašto",
        "Često, i to me zbunjuje",
        "Naglo prelazi iz nežnosti u ljutnju",
        "Nikada ne znam kakvo će mu/joj biti raspoloženje",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question: "Kako se tvoj partner odnosi prema tvojim emocijama?",
      options: [
        "Pokazuje razumevanje i empatiju",
        "Ponekad sluša, ali često menja temu na sebe",
        "Ignoriše moje emocije i umanjuje moje probleme",
        "Koristi moje emocije protiv mene",
        "Smatra da su njegove/njene emocije uvek važnije",
      ],
      scores: [0, 1, 2, 3, 3],
    },
    {
      type: "radio",
      question: "Da li tvoj partner manipuliše tvojim osećanjima?",
      options: [
        "Ne, nikada",
        "Retko, ali se dešava",
        "Koristi krivicu ili dramu da bi dobio/la šta želi",
        "Uvek menja istinu da bi izgledao/la bolje",
        "Koristi gaslighting i čini da sumnjam u sebe",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Kako tvoj partner reaguje kada se oseća ugroženo?",
      options: [
        "Otvori se i podeli osećanja",
        "Ponekad postane defanzivan/na",
        "Često me krivi za svoju nesigurnost",
        "Omalovažava druge kako bi sebe uzdigao/la",
        "Postaje agresivan/na ili pasivno-agresivan/na",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Kako tvoj partner rešava sukobe?",
      options: [
        "Otvoreno i konstruktivno",
        "Ponekad izbegava sukobe, ali ih rešavamo",
        "Ignoriše probleme dok ne nestanu",
        "Završava svađe okrivljujući mene",
        "Koristi pretnje ili manipulaciju da bi pobedio/la",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Kako se osećaš u vezi sa svojim partnerom?",
      options: [
        "Voljeno/a, podržano/a i cenjeno/a",
        "Ponekad mi nedostaje pažnja, ali imam stabilnost",
        "Često se osećam iscrpljeno/a i nesigurno",
        "Nikada ne znam kako će reagovati, uvek sam na oprezu",
        "Osećam se bezvredno, zbunjeno i iscrpljeno",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "textarea",
      question:
        "Opiši situaciju kada si se osećao/la emocionalno povređeno u vezi:",
    },
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question: "Kako tvoj partner reaguje kada nešto nije po njegovom/njenom?",
      options: [
        "Prihvata i prilagođava se",
        "Ponekad se ljuti, ali brzo pređe preko toga",
        "Ignoriše me i ponaša se hladno",
        "Pokušava da me natera da promenim mišljenje",
        "Napada, manipuliše ili preti",
      ],
      scores: [0, 1, 2, 3, 4],
    },
  ],
  copy: {
    title: "Narcisoidnost tvog partnera",
    subtitle: "Razumevanje dinamike vaše veze",
    scoreLabel: "Tvoj rezultat:",
    adviceTitle: "Saveti za tebe",
    resourcesTitle: "Korisni resursi",
    whatNextTitle: "Šta dalje?",
    shareButtonText: "Podeli rezultat",
  },
};
